import { useEffect, useState } from "react";
import QRCode from "qrcode";

/**
 * Renders a QR code from a raw payload string.
 *
 * The code is generated here, in the browser, rather than hotlinked from
 * whatever image URL the payment processor offers. That is a deliberate
 * constraint and not an optimisation: this page is the answer to "the brand
 * disappears at the moment of payment", and a page whose most important element
 * is an <img> pointing at somebody else's CDN is one rate limit, one URL
 * change, or one outage away from being a broken box where the payment used to
 * be.
 *
 * SVG rather than canvas. It stays sharp at any size — which matters, because a
 * QR is scanned off a phone screen by another phone's camera and a soft one
 * fails silently — it costs no device-pixel-ratio arithmetic, and it renders
 * identically under the prerenderer.
 *
 * Error correction level M: the middle rung. EMVCo QRIS payloads are long
 * enough that H inflates the module count until the code is dense on a small
 * screen, and there is no logo overlaid here that would need the redundancy.
 */
export function QrCode({
  payload,
  size = 260,
  className,
}: {
  payload: string;
  size?: number;
  className?: string;
}) {
  const [svg, setSvg] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    QRCode.toString(payload, {
      type: "svg",
      errorCorrectionLevel: "M",
      // No quiet-zone margin from the library: the card around it already
      // provides the white border a scanner needs, and the library's default
      // four modules shrink the code inside its own box.
      margin: 0,
      color: { dark: "#16211c", light: "#ffffff" },
    })
      .then((out) => {
        if (live) setSvg(out);
      })
      .catch(() => {
        // A payload the encoder cannot represent. Rare, but a blank square with
        // no explanation is the worst possible outcome on a payment page, so
        // the caller gets a real failure to render around.
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [payload]);

  if (failed) {
    return (
      <div
        className={className}
        style={{ width: size, height: size }}
        role="img"
        aria-label="Kode QR tidak bisa ditampilkan"
      >
        <div className="flex size-full items-center justify-center rounded-xl border border-dashed border-ink/20 p-4 text-center text-xs text-ink/50">
          Kode QR tidak bisa ditampilkan. Coba muat ulang halaman ini.
        </div>
      </div>
    );
  }

  // Until the encoder returns, hold the exact final size. A QR that pops into
  // place shifts everything under it on a phone — including, on a slow render,
  // whatever the buyer was about to tap.
  if (!svg) {
    return (
      <div className={className} style={{ width: size, height: size }} aria-hidden>
        <div className="size-full animate-pulse rounded-xl bg-ink/5" />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ width: size, height: size }}
      role="img"
      aria-label="Kode QR untuk pembayaran QRIS"
      // The SVG is generated here from a string that arrived as JSON, never as
      // markup, and the encoder emits a fixed shape — there is no path by which
      // the payload becomes an element. Parsing it into React elements instead
      // would buy nothing and cost the sharpness.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
